import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PublicShell from "@/components/PublicShell";
import { useNamedBackground } from "@/lib/sectionBackground";
import { useI18n } from "@/lib/I18nContext";
import { pickText } from "@/lib/bilingual";
import { translateAuthError } from "@/lib/authErrors";
import { ChevronRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  localStorage.removeItem("polla_invite_code");
  const redirectUrl = window.location.origin + "/perfil";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // {token} when 2FA required
  const [otp, setOtp] = useState("");
  const { login, completeLogin2FA } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const heroImage = useNamedBackground("login_hero");
  const [hero, setHero] = useState(null);
  const [heroMode, setHeroMode] = useState(null);

  useEffect(() => {
    api.get("/page-content").then((r) => {
      setHero(r.data?.login_hero);
      setHeroMode(r.data?.login_hero_mode || "text");
    }).catch(() => {
      setHeroMode("text");
    });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setChallenge({ token: result.challenge_token });
        toast.message(t("login.2faRequired"));
        return;
      }
      toast.success(t("common.welcome") + "!");
      navigate(result?.is_special_only ? "/pollas-especiales" : "/perfil");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(translateAuthError(msg, t));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit2FA = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true);
    try {
      const u = await completeLogin2FA(challenge.token, otp.trim());
      toast.success(t("common.welcome") + "!");
      navigate(u?.is_special_only ? "/pollas-especiales" : "/perfil");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(translateAuthError(msg, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      {/* HERO BLOCK */}
      {heroMode === null ? (
        <section className="max-w-3xl mx-auto px-6 pt-10 md:pt-16 pb-6 min-h-[280px] md:min-h-[360px]" data-testid="login-hero-loading" aria-hidden="true" />
      ) : heroMode !== "none" && (
        <section className="max-w-3xl mx-auto px-6 pt-10 md:pt-16 pb-6 text-center" data-testid="login-hero">
          {heroMode === "image" && heroImage ? (
            <img src={heroImage} alt="La Polla Mundialista" data-testid="login-hero-image" className="w-full h-auto" />
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-4" data-testid="login-hero-kicker">{pickText(hero?.kicker, lang) || t("login.kickerDefault")}</div>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-[1.05]" data-testid="login-hero-text">
                {(hero?.lines || [
                  { text: t("login.lineDefault1"), color: "#FFFFFF" },
                  { text: t("login.lineDefault2"), color: "var(--c-lime)" },
                  { text: t("login.lineDefault3"), color: "var(--c-sky)" },
                  { text: t("login.lineDefault4"), color: "var(--c-pink)" },
                ]).map((line, i, arr) => (
                  <span key={`${i}-${(line.text || "").slice(0, 16)}`} style={{ color: line.color }}>
                    {pickText(line.text, lang)}{i < arr.length - 1 && <br />}
                  </span>
                ))}
              </h1>
            </>
          )}
          <ScrollDownIndicator label={t("login.scrollHint")} />
        </section>
      )}

      {/* SIGN IN — vertically centered as the primary call to action (per design) */}
      <section className="max-w-md mx-auto px-6 py-6 md:py-10" data-testid="login-section">
        <div className="text-center mb-6">
          <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight" data-testid="login-headline">
            {challenge ? t("login.2faTitle") : t("login.welcomeBack")}
          </h2>
        </div>

        {challenge ? (
          <form onSubmit={onSubmit2FA} className="space-y-3" data-testid="login-2fa-form">
            <p className="text-sm text-zinc-300 text-center">{t("login.2faHint")}</p>
            <input
              data-testid="login-2fa-code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="123 456"
              aria-label={t("login.2faCode")}
              maxLength={20}
              autoFocus
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-4 py-3.5 rounded-full text-center font-mono-data tracking-widest text-lg"
              required
            />
            <button
              type="submit"
              disabled={loading || !otp.trim()}
              data-testid="login-2fa-submit"
              className="w-full bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider py-3.5 rounded-full hover:bg-lime-300 transition-colors disabled:opacity-50 text-sm"
            >
              {loading ? t("common.loading") : t("login.2faSubmit")}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setOtp(""); }}
              data-testid="login-2fa-cancel"
              className="w-full text-xs uppercase tracking-widest text-zinc-400 hover:text-white py-2">
              {t("common.cancel")}
            </button>
          </form>
        ) : (
        <>
        <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("common.email")}
            aria-label={t("common.email")}
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-4 py-3.5 rounded-full text-sm"
            required
          />
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("common.password")}
            aria-label={t("common.password")}
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-4 py-3.5 rounded-full text-sm"
            required
            minLength={4}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider py-3.5 rounded-full hover:bg-lime-300 transition-colors disabled:opacity-50 text-sm"
          >
            {loading ? t("common.loading") : t("login.loginCta")}
          </button>
        </form>

        <div className="text-center mt-3">
          <Link to="/forgot-password" data-testid="link-forgot-password" className="text-[12px] underline text-zinc-300 hover:text-white">
            {t("login.forgotPassword")}
          </Link>
        </div>

        <button
          onClick={googleLogin}
          data-testid="google-login-button"
          className="w-full mt-5 bg-white text-black font-bold uppercase tracking-wider py-3.5 rounded-full hover:bg-zinc-100 transition-colors flex items-center justify-center gap-3 text-sm"
        >
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"/></svg>
          {t("login.continueWithGoogle")}
        </button>

        <Link
          to="/register"
          data-testid="link-register"
          className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-[var(--c-sky)] text-black font-bold uppercase tracking-wider py-3.5 rounded-full hover:brightness-110 transition-all text-sm shadow-sm"
        >
          {t("common.signup")} <ChevronRight size={16} />
        </Link>
        </>
        )}
      </section>
    </PublicShell>
  );
}

function ScrollDownIndicator({ label }) {
  const onClick = () => {
    const target = document.querySelector('[data-testid="login-section"]');
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollBy({ top: window.innerHeight * 0.7, behavior: "smooth" });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid="scroll-down-indicator"
      className="mt-8 mx-auto flex flex-col items-center gap-1 text-zinc-300 hover:text-white transition-colors group"
    >
      <svg width="28" height="44" viewBox="0 0 28 44" fill="none" className="opacity-90 group-hover:opacity-100">
        <rect x="1.5" y="1.5" width="25" height="41" rx="12.5" stroke="currentColor" strokeWidth="2" />
        <circle cx="14" cy="13" r="2.2" fill="currentColor">
          <animate attributeName="cy" values="11;19;11" dur="1.6s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.4 0 0.2 1;0.4 0 0.2 1" />
          <animate attributeName="opacity" values="1;0.35;1" dur="1.6s" repeatCount="indefinite" keyTimes="0;0.5;1" />
        </circle>
      </svg>
      <div className="flex flex-col items-center" style={{ lineHeight: 0 }}>
        <svg width="20" height="10" viewBox="0 0 20 10" fill="none" className="scroll-chevron scroll-chevron-1">
          <path d="M2 2 L10 8 L18 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <svg width="20" height="10" viewBox="0 0 20 10" fill="none" className="scroll-chevron scroll-chevron-2 -mt-1">
          <path d="M2 2 L10 8 L18 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="text-[9px] uppercase tracking-widest font-mono-data text-zinc-500 mt-1">{label}</div>
    </button>
  );
}
